import { AUTH_STORAGE_KEYS } from "../config/auth";
import type { EffectiveAccess, OktaVerifiedUser } from "./authService";

export type AuthSessionInput = {
  readonly accessToken: string;
  readonly idToken: string;
  readonly expiresAt: number | null;
  readonly user: OktaVerifiedUser;
  readonly effectiveAccess: EffectiveAccess;
};

/** Okta tokens survive task pane reloads; PKCE values stay in sessionStorage only. */
const AUTH_STORE = localStorage;

function toPositiveNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function migrateAuthKeyFromSessionStorage(key: string): string | null {
  const fromSession = sessionStorage.getItem(key);
  if (fromSession == null || fromSession === "") {
    return null;
  }
  AUTH_STORE.setItem(key, fromSession);
  sessionStorage.removeItem(key);
  return fromSession;
}

function readAuthString(key: string): string | null {
  const fromLocal = AUTH_STORE.getItem(key);
  if (fromLocal != null && fromLocal.trim().length > 0) {
    return fromLocal;
  }
  return migrateAuthKeyFromSessionStorage(key);
}

function isOktaVerifiedUser(raw: unknown): raw is OktaVerifiedUser {
  if (raw == null || typeof raw !== "object") {
    return false;
  }
  const o = raw as { id?: unknown };
  return typeof o.id === "string" && o.id.trim().length > 0;
}

function isEffectiveAccess(raw: unknown): raw is EffectiveAccess {
  if (raw == null || typeof raw !== "object") {
    return false;
  }
  const o = raw as { schema_version?: unknown };
  return typeof o.schema_version === "number";
}

/** Best-effort read of the user persisted at login (Okta verify). */
export function getStoredUser(): OktaVerifiedUser | null {
  const raw = readAuthString(AUTH_STORAGE_KEYS.user);
  if (raw == null || raw === "") {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isOktaVerifiedUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function getCurrentUserId(): string | null {
  const u = getStoredUser();
  return u?.id ?? null;
}

/** Snapshot from last successful Okta verify (RBAC / `decision_role_code`). */
export function getEffectiveAccess(): EffectiveAccess | null {
  const raw = readAuthString(AUTH_STORAGE_KEYS.effectiveAccess);
  if (raw == null || raw === "") {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isEffectiveAccess(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setAuthSession(session: AuthSessionInput): void {
  AUTH_STORE.setItem(AUTH_STORAGE_KEYS.accessToken, session.accessToken);
  AUTH_STORE.setItem(AUTH_STORAGE_KEYS.idToken, session.idToken);
  if (session.expiresAt) {
    AUTH_STORE.setItem(AUTH_STORAGE_KEYS.tokenExpiresAt, String(session.expiresAt));
  } else {
    AUTH_STORE.removeItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
  }
  AUTH_STORE.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(session.user));
  AUTH_STORE.setItem(
    AUTH_STORAGE_KEYS.effectiveAccess,
    JSON.stringify(session.effectiveAccess),
  );
  for (const k of Object.values(AUTH_STORAGE_KEYS)) {
    sessionStorage.removeItem(k);
  }
}

export function getAccessToken(): string | null {
  const token = readAuthString(AUTH_STORAGE_KEYS.accessToken);
  return token && token.trim().length > 0 ? token : null;
}

export function getIdToken(): string | null {
  const token = readAuthString(AUTH_STORAGE_KEYS.idToken);
  return token && token.trim().length > 0 ? token : null;
}

export function getTokenExpiry(): number | null {
  let raw = AUTH_STORE.getItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
  if (raw == null || raw === "") {
    raw = migrateAuthKeyFromSessionStorage(AUTH_STORAGE_KEYS.tokenExpiresAt);
  }
  return toPositiveNumber(raw);
}

export function isSessionExpired(): boolean {
  const expiresAt = getTokenExpiry();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
}

export function isAuthenticated(): boolean {
  const token = getAccessToken() ?? getIdToken();
  if (!token) return false;
  return !isSessionExpired();
}

export function clearAuthSession(): void {
  for (const k of Object.values(AUTH_STORAGE_KEYS)) {
    AUTH_STORE.removeItem(k);
    sessionStorage.removeItem(k);
  }
}
